import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2, 
  Eye,
  Bell,
  RefreshCw,
  GitBranch,
  History,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';

const API_URL = window.location.origin;

const UniversityApprovals = () => {
  const { user } = useCMSAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [propagationLogs, setPropagationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewComments, setReviewComments] = useState('');
  const [processing, setProcessing] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch pending approvals
      const pendingRes = await fetch(`${API_URL}/api/university-workflow/pending-approvals`);
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingRequests(data);
      }

      // Fetch propagation logs
      const logsRes = await fetch(`${API_URL}/api/university-workflow/propagation-logs`);
      if (logsRes.ok) {
        const logs = await logsRes.json();
        setPropagationLogs(logs);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load approval data');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (action) => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/university-workflow/review/${selectedRequest.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: action,
          comments: reviewComments
        })
      });

      if (res.ok) {
        toast.success(action === 'approve' 
          ? 'Change approved and propagating across systems' 
          : 'Change request rejected');
        setReviewDialogOpen(false);
        setSelectedRequest(null);
        setReviewComments('');
        fetchData();
      } else {
        toast.error('Failed to process review');
      }
    } catch (error) {
      console.error('Error processing review:', error);
      toast.error('Failed to process review');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewDialog = (request) => {
    setSelectedRequest(request);
    setReviewComments('');
    setReviewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getChangeTypeBadge = (type) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      toggle_status: 'bg-purple-100 text-purple-800'
    };
    return <Badge className={colors[type] || 'bg-gray-100'}>{type}</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="university-approvals">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">University Change Approvals</h1>
          <p className="text-gray-600">Review and approve university changes across the platform</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{pendingRequests.length}</p>
                <p className="text-sm text-gray-600">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <GitBranch className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{propagationLogs.length}</p>
                <p className="text-sm text-gray-600">Recent Propagations</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="relative">
            Pending Approvals
            {pendingRequests.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2">
                {pendingRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="propagation">Propagation Logs</TabsTrigger>
        </TabsList>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Change Requests</CardTitle>
              <CardDescription>
                {isSuperAdmin 
                  ? 'Review and approve/reject university changes'
                  : 'Only Super Admins can approve changes'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No pending approvals</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>University</TableHead>
                      <TableHead>Change Type</TableHead>
                      <TableHead>Submitted By</TableHead>
                      <TableHead>Submitted At</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">
                          {request.changes?.university_name || 'N/A'}
                        </TableCell>
                        <TableCell>{getChangeTypeBadge(request.change_type)}</TableCell>
                        <TableCell>{request.submitted_by}</TableCell>
                        <TableCell>
                          {new Date(request.submitted_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReviewDialog(request)}
                            disabled={!isSuperAdmin}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Propagation Logs Tab */}
        <TabsContent value="propagation">
          <Card>
            <CardHeader>
              <CardTitle>Change Propagation History</CardTitle>
              <CardDescription>
                Track how approved changes propagate across the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              {propagationLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-12 w-12 mx-auto mb-4" />
                  <p>No propagation logs yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Change Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Updates Made</TableHead>
                      <TableHead>Started At</TableHead>
                      <TableHead>Completed At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {propagationLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{getChangeTypeBadge(log.change_type)}</TableCell>
                        <TableCell>
                          <Badge className={
                            log.status === 'completed' ? 'bg-green-100 text-green-800' :
                            log.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {log.updates?.slice(0, 3).map((update, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {update.target}
                              </Badge>
                            ))}
                            {log.updates?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{log.updates.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(log.started_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {log.completed_at 
                            ? new Date(log.completed_at).toLocaleString()
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Change Request</DialogTitle>
            <DialogDescription>
              Review the changes and approve or reject this request
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Change Details */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Change Type:</span>
                  {getChangeTypeBadge(selectedRequest.change_type)}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">University:</span>
                  <p className="font-semibold">{selectedRequest.changes?.university_name || 'N/A'}</p>
                </div>
                {selectedRequest.reason && (
                  <div>
                    <span className="text-sm font-medium text-gray-600">Reason:</span>
                    <p>{selectedRequest.reason}</p>
                  </div>
                )}
              </div>

              {/* Changes Preview */}
              <div>
                <h4 className="font-medium mb-2">Changes to Apply:</h4>
                <div className="bg-blue-50 p-4 rounded-lg max-h-48 overflow-auto">
                  <pre className="text-xs whitespace-pre-wrap">
                    {JSON.stringify(selectedRequest.changes, null, 2)}
                  </pre>
                </div>
              </div>

              {/* Propagation Info */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">If approved, this will automatically:</p>
                    <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                      <li>Update the university in the database</li>
                      <li>Refresh destination pages cache</li>
                      <li>Update AI Counselor knowledge base</li>
                      <li>Create/update university SEO page</li>
                      <li>Update search index</li>
                      <li>Mark related comparisons for refresh</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="text-sm font-medium">Review Comments (optional)</label>
                <Textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder="Add any comments for the submitter..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleReview('reject')}
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => handleReview('approve')}
              disabled={processing}
            >
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve & Propagate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UniversityApprovals;
